import { useCallback, useState } from "react";
import axios from "axios";
import { api } from "api";
import { adminLogin, getProfile, logoutUrl } from "api/apiURLs";
import { isDeadSessionError } from "api/authRefresh";
import { useDispatch } from "react-redux";
import { authSliceActions } from "store/authSlice";
import { useNavigate } from "react-router-dom";
import { getStoredOriginalToken, getStoredToken } from "utils/auth";

const useLogin = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [user, setUser] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const login = useCallback(
        async (credentials) => {
            setLoading(true);
            setError(null);

            try {
                const response = await api.post(
                    adminLogin,
                    { ...credentials, supportsRefresh: true },
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                    },
                );

                setUser(response.data.user); // Assuming the API returns the user data

                dispatch(authSliceActions.login(response.data.user));

                navigate("/");

                return response.data; // You can return any response data if needed
            } catch (err) {
                setError(err.response?.data?.message || "An error occurred"); // Extract API error message
                throw err; // Optionally re-throw the error for further handling
            } finally {
                setLoading(false);
            }
        },
        [dispatch, navigate],
    );

    const getProfileBytoken = useCallback(async () => {
        const token = getStoredToken();

        if (!token) {
            dispatch(authSliceActions.logout());
            return null;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await api.get(getProfile);

            setUser(response.data); // Assuming the API returns the user data

            dispatch(authSliceActions.hydrateProfile(response.data));

            return response.data; // You can return any response data if needed
        } catch (err) {
            setError(err.response?.data?.message || err.message || "An error occurred");
            // Only a dead session ends it: the API's 401 TOKEN_INVALID, or the
            // profile route's 404 "notfound" / 500 "unauthorized" for a token
            // it cannot use (see api/authRefresh.js). Network errors, 429 and
            // other 5xx keep the credentials, and ProtectedRoute offers a retry.
            if (isDeadSessionError(err)) {
                dispatch(authSliceActions.logout());
            }
            return null;
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    const logout = useCallback(async () => {
        // Capture before either request can clear storage. During
        // impersonation there are two distinct server sessions to revoke.
        const originalToken = getStoredOriginalToken();
        let currentSessionRevoked = false;
        let originalSessionRevoked = !originalToken;

        try {
            await api.post(logoutUrl, {});
            currentSessionRevoked = true;
        } catch {
            // Local logout still proceeds. The API now reports revocation DB
            // failures instead of a false 200, so callers can distinguish this
            // best-effort fallback if they need to surface it later.
        }

        if (originalToken) {
            try {
                await axios.post(
                    logoutUrl,
                    {},
                    {
                        baseURL: process.env.REACT_APP_API_URL,
                        headers: {
                            Authorization: `Bearer ${originalToken}`,
                            "X-Client": "dashboard_web",
                        },
                    },
                );
                originalSessionRevoked = true;
            } catch {
                // Same local-first guarantee as the current session.
            }
        }

        setUser(null); // Clear user data
        dispatch(authSliceActions.logout());
        return { currentSessionRevoked, originalSessionRevoked };
    }, [dispatch]);

    return { user, loading, error, login, logout, getProfileBytoken };
};

export default useLogin;
